import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CalendarClock, Settings2, Wallet } from 'lucide-react';
import type { PayrollDuration, PayrollRun, PayrollRunRow } from '@tailoring-crm/shared';
import { EmptyState } from '../feedback/empty-state.js';
import { Button, Skeleton, cn } from '@tailoring-crm/ui';
import { useAuth } from '../../lib/auth/auth-context.js';
import { isPayrollCycleUnconfigured } from '../../lib/payroll/payroll-api.js';
import {
  usePayrollCycle,
  usePayrollRun,
  usePayrollRunHistory,
} from '../../lib/payroll/use-payroll.js';
import { formatCurrency, formatDate } from '../../lib/format.js';
import { PayrollAddOnDialog } from './payroll-add-on-dialog.js';
import { PayrollAdvanceDialog } from './payroll-advance-dialog.js';
import { PayrollCycleDialog } from './payroll-cycle-dialog.js';
import { PayrollRunTable } from './payroll-run-table.js';

/**
 * The payroll half of the wage screen: the cycle this shop pays on, and what each person takes
 * home in it.
 *
 * It sits BELOW the settlement list rather than beside it behind a tab, because the two answer
 * different questions and a shop on a payroll cycle still settles piece-rate work. Nothing above
 * it moves.
 *
 * The one refusal worth naming here is the unconfigured cycle. A shop that has never set a
 * cycle up, and a shop whose run is refused by the server's gate for the same reason, are the
 * SAME situation and land on the same empty state — with the action that fixes it. Neither is
 * allowed to reach the shop as a red error: "something went wrong" is false, and there is
 * nothing to retry.
 */

const TABS = ['current', 'past'] as const;
type PayrollTab = (typeof TABS)[number];

/** Held in the URL so a reload and the back button keep the tab the shop was on. */
const TAB_PARAM = 'payrollTab';

/** What the empty state and the settings dialog assume before a shop has said otherwise. */
const FALLBACK_DURATION: PayrollDuration = 'monthly';

interface EntryDialogTarget {
  kind: 'advance' | 'addOn';
  staffUserId: string;
  staffName: string;
}

export function PayrollSection(): ReactElement | null {
  const { t } = useTranslation();
  const { role } = useAuth();
  const [params, setParams] = useSearchParams();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [entry, setEntry] = useState<EntryDialogTarget | null>(null);
  const [openPastPeriod, setOpenPastPeriod] = useState<string | null>(null);

  /** Reading payroll is the settle set; configuring it and moving money is the adjust set. */
  const canRead = role === 'owner' || role === 'manager';
  const canWrite = role === 'owner';

  const tab: PayrollTab = params.get(TAB_PARAM) === 'past' ? 'past' : 'current';

  const cycle = usePayrollCycle({ enabled: canRead });
  const cycleUnconfigured = isPayrollCycleUnconfigured(cycle.error) || cycle.data?.active === false;

  const run = usePayrollRun({ enabled: canRead && cycle.isSuccess && !cycleUnconfigured });
  const unconfigured = cycleUnconfigured || isPayrollCycleUnconfigured(run.error);

  const history = usePayrollRunHistory(run.data?.periodFrom ?? null, {
    enabled: tab === 'past' && !unconfigured,
  });

  if (!canRead) return null;

  const duration = cycle.data?.duration ?? run.data?.duration ?? FALLBACK_DURATION;

  const selectTab = (next: PayrollTab): void => {
    const nextParams = new URLSearchParams(params);
    if (next === 'current') nextParams.delete(TAB_PARAM);
    else nextParams.set(TAB_PARAM, next);
    setParams(nextParams, { replace: true });
  };

  const rowActions = {
    canWrite,
    onRecordAdvance: (row: PayrollRunRow) =>
      setEntry({ kind: 'advance', staffUserId: row.staffUserId, staffName: row.staffName }),
    onAddAddOn: (row: PayrollRunRow) =>
      setEntry({ kind: 'addOn', staffUserId: row.staffUserId, staffName: row.staffName }),
  };

  const openPast = openPastPeriod
    ? (history.data ?? []).find((one) => one.periodFrom === openPastPeriod)
    : undefined;

  return (
    <section className="flex flex-col gap-4 border-t border-border pt-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{t('payroll.heading')}</h2>
          <p className="text-sm text-muted-foreground">{t('payroll.description')}</p>
        </div>
        {canWrite ? (
          <Button variant="outline" onClick={() => setSettingsOpen(true)}>
            <Settings2 aria-hidden="true" />
            {t('payroll.cycleSettings')}
          </Button>
        ) : null}
      </div>

      <div
        role="tablist"
        aria-label={t('payroll.heading')}
        className="flex gap-2 overflow-x-auto pb-1"
      >
        {TABS.map((one) => (
          <button
            key={one}
            type="button"
            role="tab"
            aria-selected={tab === one}
            onClick={() => selectTab(one)}
            className={cn(
              'flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              tab === one
                ? 'border-transparent bg-primary text-primary-foreground'
                : 'border-border hover:bg-accent',
            )}
          >
            {t(`payroll.tab.${one}`)}
          </button>
        ))}
      </div>

      {unconfigured ? (
        <EmptyState
          icon={CalendarClock}
          title={t('payroll.noCycleTitle', { duration: t(`payroll.durationInline.${duration}`) })}
          description={t('payroll.noCycleHint')}
          {...(canWrite
            ? {
                action: (
                  <Button onClick={() => setSettingsOpen(true)}>{t('payroll.configureNow')}</Button>
                ),
              }
            : {})}
        />
      ) : cycle.isError || run.isError ? (
        <EmptyState icon={Wallet} title={t('error.unexpected')} />
      ) : !run.data ? (
        <PayrollSkeleton />
      ) : tab === 'current' ? (
        <>
          <CycleSummary run={run.data} />
          {run.data.rows.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={t('payroll.emptyRunTitle')}
              description={t('payroll.emptyRunHint')}
            />
          ) : (
            <PayrollRunTable run={run.data} {...rowActions} />
          )}
        </>
      ) : history.data === undefined ? (
        <PayrollSkeleton />
      ) : history.data.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title={t('payroll.pastEmptyTitle')}
          description={t('payroll.pastEmptyHint')}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-2">
            {history.data.map((past) => (
              <li
                key={past.periodFrom}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
              >
                <div className="min-w-0">
                  <p className="font-medium">
                    {t('payroll.period', {
                      from: formatDate(past.periodFrom),
                      to: formatDate(past.periodTo),
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground tabular-nums">
                    {t('payroll.pastCycleTotals', {
                      salary: formatCurrency(past.totals.totalSalaryPaise),
                      advance: formatCurrency(past.totals.advancePaise),
                      addOn: formatCurrency(past.totals.addOnPaise),
                      payable: formatCurrency(past.totals.payablePaise),
                    })}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  aria-pressed={openPastPeriod === past.periodFrom}
                  onClick={() => setOpenPastPeriod(past.periodFrom)}
                >
                  {t('payroll.viewPastCycle')}
                </Button>
              </li>
            ))}
          </ul>

          {openPast ? (
            openPast.rows.length === 0 ? (
              <EmptyState
                icon={Wallet}
                title={t('payroll.emptyRunTitle')}
                description={t('payroll.emptyRunHint')}
              />
            ) : (
              <PayrollRunTable run={openPast} {...rowActions} />
            )
          ) : null}
        </>
      )}

      {settingsOpen ? (
        <PayrollCycleDialog
          cycle={cycle.data ?? null}
          fallbackDuration={duration}
          onClose={() => setSettingsOpen(false)}
        />
      ) : null}

      {entry?.kind === 'advance' ? (
        <PayrollAdvanceDialog
          staffUserId={entry.staffUserId}
          staffName={entry.staffName}
          onClose={() => setEntry(null)}
        />
      ) : null}

      {entry?.kind === 'addOn' ? (
        <PayrollAddOnDialog
          staffUserId={entry.staffUserId}
          staffName={entry.staffName}
          onClose={() => setEntry(null)}
        />
      ) : null}
    </section>
  );
}

/** What the shop is on, and the window in hand — read from the run, never worked out here. */
function CycleSummary({ run }: { run: PayrollRun }): ReactElement {
  const { t } = useTranslation();
  return (
    <p className="text-sm text-muted-foreground">
      {t('payroll.cycleSummary', {
        mode: t(`payroll.mode.${run.mode}`),
        duration: t(`payroll.durationInline.${run.duration}`),
      })}
      {' · '}
      {t('payroll.period', { from: formatDate(run.periodFrom), to: formatDate(run.periodTo) })}
    </p>
  );
}

/**
 * Deliberately not one of the `role="status"` skeletons: this section shares a page with the
 * outstanding-liability panel, which IS a status region, and two of them would leave a screen
 * reader — and the page's own tests — with two answers to "what is the state of this screen?".
 */
function PayrollSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-2">
      {[0, 1, 2].map((one) => (
        <Skeleton key={one} className="h-12 w-full" />
      ))}
    </div>
  );
}
